import Tooltip from '@mui/material/Tooltip';

import { ClauseTooltipContent } from './clause-tooltip-content.js';

import type { TooltipProps } from '@mui/material/Tooltip';
import type { ClauseTooltipContentProps } from './clause-tooltip-content.js';

export interface ClauseTooltipProps extends ClauseTooltipContentProps {
	/** The element that shows the tooltip on hover. It must accept a ref. */
	children: TooltipProps['children'];
	placement?: TooltipProps['placement'];
	/** Where to show the tooltip, in order, when `placement` has no room for it. */
	fallbackPlacements?: NonNullable<TooltipProps['placement']>[];
	/** Closes the tooltip as soon as the pointer leaves the anchor, rather than letting it be hovered. */
	disableInteractive?: boolean;
}

/**
 * A hover tooltip describing one clause version: its number, version, author, short name, and full
 * description.
 *
 * @param props - {@link ClauseTooltipProps}.
 * @returns The anchor wrapped in the tooltip.
 */
export function ClauseTooltip({
	children,
	placement,
	fallbackPlacements,
	disableInteractive = false,
	...contentProps
}: ClauseTooltipProps) {
	return (
		<Tooltip
			title={<ClauseTooltipContent {...contentProps} />}
			enterDelay={400}
			placement={placement}
			disableInteractive={disableInteractive}
			// Wide enough that a long description wraps to a few lines rather than a narrow column.
			slotProps={{
				tooltip: { sx: { maxWidth: '28rem' } },
				// Only the side it opens on decides a fallback; overflow along the anchor is shifted back on screen.
				popper:
					fallbackPlacements === undefined ? undefined : (
						{ modifiers: [{ name: 'flip', options: { fallbackPlacements, altAxis: false } }] }
					),
			}}
		>
			{children}
		</Tooltip>
	);
}
